import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from '../../lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg text-button font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-none hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-none hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-none hover:bg-accent hover:text-accent-foreground",
        primaryOutline:
          "border border-primary bg-background text-primary shadow-none hover:bg-primary/5 hover:text-primary",
        secondary:
          "bg-secondary text-secondary-foreground shadow-none hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        /** Elevated tile for standalone icon actions — border, background, subtle shadow */
        iconAction:
          "border border-shell-border bg-background text-foreground shadow-[0_1px_2px_rgba(0,0,0,0.06),0_2px_4px_rgba(0,0,0,0.04)] hover:bg-muted/40 active:shadow-[0_1px_1px_rgba(0,0,0,0.04)] dark:shadow-[0_1px_3px_rgba(0,0,0,0.28)]",
      },
      size: {
        default: "h-8 px-2.5",
        sm: "h-7 px-2 text-body-sm",
        lg: "h-9 px-4",
        icon: "h-8 w-8 p-0",
        /** Social header icon control — fixed 32px hit target, 20px glyph */
        "icon-md": "size-header-icon shrink-0 p-0 [&_svg]:size-icon-lg [&_svg]:shrink-0",
        /** Elevated icon tile — 36px squircle with 20px glyph */
        "icon-action": "size-9 shrink-0 rounded-xl p-0 [&_svg]:size-icon-lg [&_svg]:shrink-0",
        /** Compact elevated icon tile — 32px */
        "icon-action-sm": "size-8 shrink-0 rounded-xl p-0 [&_svg]:size-4 [&_svg]:shrink-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
